import { useCallback, useMemo } from 'react'
import useNavigation from './useNavigation'
import useSessionStorage from 'core/hooks/useSessionStorage'

export type UseUrlSearchReturn = [
  URLSearchParams,
  (d: URLSearchParams, pathname?: string) => void
]

/**
 * @description: A convenient hook that allows reading and writing into the current URL query params, typically used for storing view states like page limit, filtering, sorting info, etc; This also wires with the global client state to store the path and url specific association. Read more on useSettings' type definition.
 * @example: To read the current page url query, use the first element of the returned tuple. To write into the current page url query, call the 2nd element of the tuple with the changed. Note that this hook works with Javascript URLSearchParams and not a plain object.
 * @returns: A tuple [urlParams, setURLParams]. The first is an URLSearchParams object, the second is a function to modify the page, with the argument being the new URLSearchParams. Calling the setter will push to the history stack.
 */
const useUrlSearch = () => {
  const { search, replace, pathname } = useNavigation()

  const [pageUrlQueries, setUrlQueries] = useSessionStorage(
    'url_queries',
    {} as Record<string, string>
  )

  const setQueryParams = useCallback(
    (params: URLSearchParams, newPath = pathname) => {
      setUrlQueries({
        ...pageUrlQueries,
        [newPath]: params.toString()
      })
      replace({
        pathname: newPath,
        search: params.toString()
      })
    },
    [pathname, replace, setUrlQueries, pageUrlQueries]
  )
  const searchParams = useMemo(() => {
    return new URLSearchParams(search || pageUrlQueries[pathname] || '')
  }, [pageUrlQueries, pathname, search])

  return [searchParams, setQueryParams] as UseUrlSearchReturn
}

export default useUrlSearch
